import { TranslationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { SegmentedControl, SegmentedControlItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { useLoader, useModal, useTranslation } from '@hooks';
import { useSearchParamsStore } from '@store';

import { EnIcon, EsIcon, PtIcon } from '../icons';

interface LanguageOption {
	code?: string;
	name?: string;
}

interface LanguageSelectionProps {
	size?: 'small' | 'regular';
}

export const LanguageSelection = ({ size = 'small' }: LanguageSelectionProps) => {
	const { t, fetchContents } = useTranslation();
	const globalLoader = useLoader('global');
	const genericErrorModal = useModal('genericError');
	const selectedLanguage = useSearchParamsStore((state) => state.language);
	const setSearchParamsStore = useSearchParamsStore((state) => state.setState);

	const languages = getParsedLanguages(t('general.languages'));
	const clientAvailableLanguages = getParsedAvailableLanguages(t('configs.availableLanguages'));

	const selectedLanguageIndex = languages.findIndex(
		(lang) => lang.code?.toLowerCase() === selectedLanguage?.toLowerCase()
	);

	const handleLanguageClick = async (index: number) => {
		/* Do nothing when clicked same selected language */
		if (selectedLanguageIndex === index) {
			return;
		}

		const newLanguage = languages[index].code?.toLowerCase() as Language;
		try {
			// show global loader
			globalLoader.show();
			// fetch new contents
			await fetchContents(newLanguage);
			// change language query param
			const params = new URLSearchParams(window.location.search);
			params.set('language', newLanguage);
			window.history.pushState(null, '', `?${params.toString()}`);
			setSearchParamsStore({ language: newLanguage });
			// change Translation language
			TranslationHelper.changeLanguage(newLanguage);
		} catch {
			genericErrorModal.show();
		} finally {
			globalLoader.hide();
		}
	};

	const items = getLanguageItems(
		languages,
		clientAvailableLanguages,
		size,
		selectedLanguageIndex,
		handleLanguageClick
	);

	if (!items.length) {
		return null;
	}

	return <SegmentedControl variant="light" size="small" items={items} itemActiveIndex={selectedLanguageIndex} />;
};

const getParsedLanguages = (languagesArray: string): LanguageOption[] => {
	try {
		const parsedLanguages = JSON.parse(languagesArray);
		if (!Array.isArray(parsedLanguages)) {
			return [];
		}

		parsedLanguages.sort((a, b) => {
			// First, prioritize "PT" and "EN"
			if (a.code === 'PT') return -1;
			if (b.code === 'PT') return 1;
			if (a.code === 'EN') return -1;
			if (b.code === 'EN') return 1;

			// Then, sort the rest alphabetically by "name"
			return a.name.localeCompare(b.name);
		});

		return parsedLanguages;
	} catch (error) {
		return [];
	}
};

const getParsedAvailableLanguages = (availableLanguagesArray: string): string[] => {
	try {
		const parsedAvailableLanguages = JSON.parse(availableLanguagesArray);
		if (!Array.isArray(parsedAvailableLanguages)) {
			return [];
		}

		return parsedAvailableLanguages;
	} catch (error) {
		return [];
	}
};

const getLanguageItems = (
	languages: LanguageOption[],
	clientAvailableLanguages: string[],
	size?: 'small' | 'regular',
	selectedLanguageIndex?: number,
	handleOnClick?: (index: number) => void
): SegmentedControlItem[] => {
	const getIconSource = (code?: string) => {
		switch (code) {
			case 'ES':
				return EsIcon;
			case 'EN':
				return EnIcon;
			default:
				return PtIcon;
		}
	};

	return languages
		.filter((lang) => clientAvailableLanguages.find((clientLang) => clientLang === lang.code))
		.map((lang, index) => {
			return {
				text: size === 'small' || index !== selectedLanguageIndex ? lang.code : lang.name,
				...(index === selectedLanguageIndex && { icon: { source: getIconSource(lang.code) } }),
				onPress: () => handleOnClick?.(index),
			};
		});
};
